import AgTypography from '../../../Styles/AgTypography';
import { AgColor } from '../../../Styles/AgColor';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {
  useState,
  cloneElement,
  MouseEvent,
  CSSProperties,
  ReactElement,
  MouseEventHandler,
  ReactNode
} from 'react';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { SxProps, Theme } from '@mui/material';
import { useIntl } from 'react-intl';

const AgTopNavigation = (
  {
    profileMenuItems,
    helpMenuItems,
    navItems,
    showIcon = true,
    SxProps,
    breadCrumb
  }: {
    profileMenuItems: {
      icon?: ReactElement;
      name?: string;
      onClick?: (() => void | MouseEventHandler<HTMLButtonElement> | Promise<void>) | undefined;
      style?: CSSProperties;
      component?: ReactNode;
    }[];
    helpMenuItems?: {
      icon?: ReactElement;
      name?: string;
      onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
      style?: CSSProperties;
      component?: ReactNode;
    }[];
    navItems: {
      icon?: ReactElement;
      name?: string;
      onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
    }[];
    showIcon?: boolean;
    SxProps?:SxProps<Theme>;
    breadCrumb?: ReactNode
  }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElHelp, setAnchorElHelp] = useState<null | HTMLElement>(null);
  const messages = useIntl().formatMessage;
  const isMenuOpen = Boolean(anchorEl);
  const isHelpMenuOpen = Boolean(anchorElHelp);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleHelpMenuClose = () => {
    setAnchorElHelp(null);
  };

  const handleProfileMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleHelpMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorElHelp(event.currentTarget);
  };

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      id='profileMenu'
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      sx={{
        '& .MuiPaper-root': {
          boxShadow: 'none',
          border: `1px solid ${AgColor.grey3}`,
          width: '168px',
          borderRadius: '8px',
          padding: '4px 0'
        },
        '& .MuiMenu-list': {
          padding: '0'
        }
      }}
    >
      {profileMenuItems.map((item, index) => {
        return (
          item.component ? (
            <MenuItem key={index} sx={{ padding: '0', cursor: 'default' }} disableRipple>
              {item.component}
            </MenuItem>
          ) : (
            <MenuItem
              key={index}
              onClick={()=>{
                if (item.onClick) item.onClick();
                handleMenuClose();
              }}
              style={{
                padding: '10px 0',
                backgroundColor: AgColor.grey0,
                ...item.style
              }}
            >
              <AgTypography //in general, we have 3 layers  to wrap up the icon as per design on figma
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '3px 16px'
                }}
              >
                <AgTypography
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '16px',
                    height: '16px',
                    color: AgColor.secondaryDark
                  }}
                >
                  {item.icon &&
                    cloneElement(item.icon, { style: { fontSize: '16px' } })}
                </AgTypography>
                <AgTypography
                  contentStyle={'AP.Paragraph.Small.Link'}
                  onClick={item.onClick}
                  style={{
                    color: AgColor.secondaryDark
                  }}
                >
                  {item.name}
                </AgTypography>
              </AgTypography>
            </MenuItem>
          )
        );
      })}
    </Menu>
  );

  const renderHelpMenu = (
    <Menu
      anchorEl={anchorElHelp}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      id='profileMenu'
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      open={isHelpMenuOpen}
      onClose={handleHelpMenuClose}
      sx={{
        '& .MuiPaper-root': {
          boxShadow: 'none',
          border: `1px solid ${AgColor.grey3}`,
          width: '168px',
          borderRadius: '8px',
          padding: '4px 0'
        },
        '& .MuiMenu-list': {
          padding: '0'
        }
      }}
    >
      {helpMenuItems?.map((item, index) => {
        return (
          item.component ?? (
            <MenuItem
              key={index}
              onClick={handleHelpMenuClose}
              style={{
                padding: '10px 0',
                backgroundColor: AgColor.grey0,
                ...item.style
              }}
            >
              <AgTypography
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '3px 16px'
                }}
              >
                <AgTypography
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '16px',
                    height: '16px',
                    color: AgColor.secondaryDark
                  }}
                >
                  {item.icon &&
                    cloneElement(item.icon, { style: { fontSize: '16px' } })}
                </AgTypography>
                <AgTypography
                  contentStyle={'AP.Paragraph.Small.Link'}
                  onClick={item.onClick}
                  style={{
                    color: AgColor.secondaryDark
                  }}
                >
                  {item.name}
                </AgTypography>
              </AgTypography>
            </MenuItem>
          )
        );
      })}
    </Menu>
  );

  return (
    <AgTypography
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '40px',
        padding: '2px 32px',
        gap: '10px',
        color: AgColor.grey0,
        boxShadow: '0 1px 2px #0000000D',
      }}
      sx={SxProps}
    >
      <AgTypography>
        {showIcon && navItems?.map((i, index) => {
          if (i.name === 'menu')
            return (
              <AgTypography key={index} onClick={i?.onClick}>
                {i.icon}
              </AgTypography>
            );
          else return;
        })}
        {breadCrumb && breadCrumb } 
      </AgTypography>

      <AgTypography
        style={{
          display: 'flex'
        }}
      >
        {navItems?.map((item, index) => {
          if (item.name === 'menu') return;
          return (
            <AgTypography
              onClick={item?.onClick}
              key={index}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px 12px',
                gap: '8px',
                fontSize: '12px',
                lineHeight: '16px',
                letterSpacing: '3%',
                fontFamily: 'Roboto',
                fontWeight: 400,
                color: AgColor.primaryMain,
                cursor: 'pointer'
              }}
            >
              {item.icon}
              {item.name}
            </AgTypography>
          );
        })}
        {helpMenuItems?.length && helpMenuItems?.length > 0 &&<AgTypography
          onClick={handleHelpMenuOpen}
          style={{
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '16px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '8px 12px',
            color: isHelpMenuOpen ? AgColor.primaryHighlight : AgColor.primaryMain,
            cursor: 'pointer'
          }}
        >
          <HelpOutlineIcon
            style={{
              width: '15px',
              height: '15px',
              cursor: 'pointer',
              marginRight: '8px'
            }}
          /> {messages({ id: 'HELP' })}
        </AgTypography>}
        <AgTypography
          onClick={handleProfileMenuOpen}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '8px 12px',
            color: isMenuOpen ? AgColor.primaryHighlight : AgColor.primaryMain
          }}
        >
          <AccountCircleOutlinedIcon
            style={{
              width: '15px',
              height: '15px',
              cursor: 'pointer'
            }}
          />
        </AgTypography>
      </AgTypography>
      {renderMenu}
      {renderHelpMenu}
    </AgTypography>
  );
};

export default AgTopNavigation;
